import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { api } from "@/lib/api";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, AButton, Badge, StatCard, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";

const LABEL = { out_of_stock: "Out of stock", few_left: "Few left" };

function exportCsv(items) {
  const head = ["Product", "Category", "Remaining", "Issued", "Threshold", "Status"];
  const lines = items.map((r) => [r.name, r.category, r.stock, r.issued_stock, r.threshold, LABEL[r.status]].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
  const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `low-stock-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
}

function ReportRow({ r }) {
  const { c } = useAdminTheme();
  const navigate = useNavigate();
  const pct = Math.min(100, Math.round((r.stock / Math.max(r.issued_stock, 1)) * 100));
  return (
    <div data-testid={`stock-report-row-${r.id}`} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", borderBottom: `1px solid ${c.divider}`, flexWrap: "wrap" }}>
      <div style={{ width: 44, height: 44, borderRadius: 8, overflow: "hidden", background: c.surfaceAlt, flexShrink: 0 }}>
        {r.image && <img src={r.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <div className="clamp-1" style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
        <div style={{ fontSize: 12, color: c.mutedText, textTransform: "capitalize" }}>{r.category?.replace(/-/g, " ")}</div>
        {r.low_variants.length > 0 && <div style={{ fontSize: 11.5, color: c.warning, marginTop: 3 }}>{r.low_variants.map((v) => `${v.name}: ${v.stock}`).join(" · ")}</div>}
      </div>
      <div style={{ minWidth: 150 }}>
        <div style={{ fontSize: 12.5, color: c.onSurfaceSecondary }} data-testid={`stock-report-qty-${r.id}`}><b style={{ color: c.onSurface }}>{r.stock}</b> of {r.issued_stock} left · alert at ≤{r.threshold}</div>
        <div style={{ height: 5, borderRadius: 4, background: c.surfaceAlt, marginTop: 6 }}><div style={{ width: `${pct}%`, height: "100%", borderRadius: 4, background: r.status === "out_of_stock" ? c.danger : c.warning }} /></div>
      </div>
      <Badge tone={r.status === "out_of_stock" ? "danger" : "warning"}>{LABEL[r.status]}</Badge>
      <AButton variant="soft" data-testid={`stock-report-restock-${r.id}`} onClick={() => navigate(`/admin/product/${r.id}/edit`)}>Restock</AButton>
    </div>
  );
}

export default function AdminStockReport() {
  const { c } = useAdminTheme();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api("/admin/reports/low-stock").then(setData); }, []);
  if (!data) return <Spinner full />;

  const shown = data.items.filter((r) => filter === "all" || r.status === filter);
  const filters = [["all", "All alerts"], ["out_of_stock", "Out of stock"], ["few_left", "Few left"]];

  return (
    <div className="fade-up">
      <AdminPageTitle title="Low-stock report" subtitle="Products at or below the Few-Stocks-Left threshold (10% of issued stock)"
        action={<AButton variant="outline" style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="stock-report-export" onClick={() => exportCsv(shown)} disabled={!shown.length}><Download size={14} /> Export CSV</AButton>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 }}>
        <div data-testid="stock-report-out-count"><StatCard label="Out of stock" value={data.summary.out_of_stock} /></div>
        <div data-testid="stock-report-few-count"><StatCard label="Few left" value={data.summary.few_left} /></div>
        <StatCard label="Healthy" value={data.summary.total_products - data.items.length} sub={`of ${data.summary.total_products} products`} />
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {filters.map(([id, label]) => (
          <button key={id} data-testid={`stock-report-filter-${id}`} onClick={() => setFilter(id)}
            style={{ padding: "7px 14px", borderRadius: 999, border: `1px solid ${filter === id ? c.primary : c.border}`, background: filter === id ? c.primarySoft : "transparent", color: filter === id ? c.primarySoftText : c.onSurfaceSecondary, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>{label}</button>
        ))}
      </div>
      <ACard style={{ padding: 0, overflow: "hidden" }}>
        {shown.length ? shown.map((r) => <ReportRow key={r.id} r={r} />)
          : <div data-testid="stock-report-empty" style={{ padding: 36, textAlign: "center", color: c.mutedText }}>All stocked up — nothing needs restocking.</div>}
      </ACard>
    </div>
  );
}
